/* eslint-disable @typescript-eslint/no-explicit-any */
// src/pages/GroupOrderDetails.tsx
import {
  Box,
  Grid,
  GridItem,
  HStack,
  Image,
  Stack,
  Text,
  VStack,
  Spinner,
  Center,
  Divider,
} from '@chakra-ui/react';

import { useGetGroupOrderById } from '@/services/OrderManagement';
import paypalImage from '@/assets/png/paypal.png';
import { getCurrencySymbol } from '@/utils/currencyUtils';
import { GroupOrderDetailsSection } from '../GroupOrderDetailsSection';
import { TAX_RATE } from '@/utils/constants';

interface GroupOrderDetailsProps {
  id: string;
}

const GroupOrderDetails: React.FC<GroupOrderDetailsProps> = ({ id }) => {
  const { data: rawOrder, isLoading, isError } = useGetGroupOrderById(id);

  if (isLoading) {
    return (
      <Center h="60vh">
        <Spinner size="xl" />
      </Center>
    );
  }

  if (isError || !rawOrder) {
    return (
      <Box py={16} textAlign="center">
        <Text fontSize="xl" fontWeight="semibold" color="text.primary">
          Group Order Not Found
        </Text>
        <Text fontSize="sm" color="text.secondary" mt={2}>
          Unable to load order details. Please try again.
        </Text>
      </Box>
    );
  }

  const currency = rawOrder.received_currency || rawOrder.currency || 'USD';

  const itemsSummary = rawOrder.purchased_items?.reduce(
    (acc: any, item: any) => {
      const txn = item.order_tracking?.individual_txn;
      const cart = item.cart_item;
      if (!txn && !cart) return acc;

      const qty = txn?.quantity || cart?.quantity || 1;
      const unitPrice = parseFloat(txn?.unit_price || '0');
      const customizedPrice = parseFloat(txn?.customized_price || '0');
      const addOnPrice = parseFloat(txn?.add_ons_price || '0');

      const isCustomized = txn?.is_customized ?? cart?.is_customized ?? false;

      acc.totalQuantity += qty;
      acc.productTotal += unitPrice * qty;
      acc.customizedTotal += customizedPrice * qty;
      acc.addOnTotal += addOnPrice * qty;

      if (isCustomized) {
        acc.customizedQuantity += qty;
      }

      // NEW: Count add-ons from customized_fields.ProductAddOn_ids
      if (isCustomized && cart?.customized_fields?.ProductAddOn_ids?.length > 0) {
        const addOnCount = cart.customized_fields.ProductAddOn_ids.length;
        acc.totalAddOnCount += addOnCount * qty; // multiply by quantity if multiple same items
      }

      return acc;
    },
    {
      totalQuantity: 0,
      customizedQuantity: 0,
      productTotal: 0,
      customizedTotal: 0,
      addOnTotal: 0,
      totalAddOnCount: 0, // ← new field
    },
  ) || {
    totalQuantity: 0,
    customizedQuantity: 0,
    productTotal: 0,
    customizedTotal: 0,
    addOnTotal: 0,
    totalAddOnCount: 0,
  };

  const baseSubtotal =
    itemsSummary.productTotal + itemsSummary.customizedTotal + itemsSummary.addOnTotal;
  const estimatedTax = baseSubtotal * TAX_RATE;
  const grandTotal = baseSubtotal + estimatedTax;

  const totalAmount = grandTotal;

  return (
    <Box px={{ base: 6, xl: 0 }} maxW="container.xl" mx="auto" py={6}>
      <Stack spacing={6}>
        <GroupOrderDetailsSection order={rawOrder} totalAmount={totalAmount} />

        <Grid templateColumns={{ base: '1fr', lg: 'repeat(3, 1fr)' }} gap={6}>
          {/* Shipping Address */}
          <GridItem
            border="1px solid"
            borderColor="border.default"
            borderRadius="lg"
            p={4}
            bg="white"
          >
            <Text fontWeight="semibold" mb={3}>
              Ship To
            </Text>
            <Text fontSize="sm" color="text.secondary">
              {rawOrder.shipment_address?.name}
            </Text>
          </GridItem>

          {/* Payment Method */}
          <GridItem
            border="1px solid"
            borderColor="border.default"
            borderRadius="lg"
            p={4}
            bg="white"
          >
            <Text fontWeight="semibold" mb={3}>
              Payment Method
            </Text>
            <HStack>
              <Image src={paypalImage} alt="PayPal" w="70px" />
              <Text>PayPal</Text>
            </HStack>
          </GridItem>

          {/* Order History */}
          <GridItem
            border="1px solid"
            borderColor="border.default"
            borderRadius="lg"
            p={4}
            bg="white"
          >
            <Text fontWeight="semibold" mb={3}>
              Order History
            </Text>

            <VStack align="stretch" spacing={2}>
              <HStack justify="space-between">
                <Text fontSize="sm" color="text.secondary">
                  Product Price (x{itemsSummary.totalQuantity})
                </Text>
                <Text fontSize="sm" fontWeight="semibold">
                  {getCurrencySymbol(currency)}
                  {itemsSummary.productTotal.toFixed(2)}
                </Text>
              </HStack>

              {itemsSummary.customizedQuantity > 0 && (
                <HStack justify="space-between">
                  <Text fontSize="sm" color="text.secondary">
                    Customization (x{itemsSummary.customizedQuantity})
                  </Text>
                  <Text fontSize="sm" fontWeight="semibold">
                    {getCurrencySymbol(currency)}
                    {itemsSummary.customizedTotal.toFixed(2)}
                  </Text>
                </HStack>
              )}

              {itemsSummary.addOnTotal > 0 && (
                <HStack justify="space-between">
                  <Text fontSize="sm" color="text.secondary">
                    Add-ons
                    {itemsSummary.totalAddOnCount > 0 && (
                      <Text as="span" color="text.secondary">
                        {' (x' + itemsSummary.totalAddOnCount + ')'}
                      </Text>
                    )}
                  </Text>
                  <Text fontSize="sm" fontWeight="semibold">
                    {getCurrencySymbol(currency)}
                    {itemsSummary.addOnTotal.toFixed(2)}
                  </Text>
                </HStack>
              )}

              <HStack justify="space-between">
                <Text fontSize="sm" color="text.secondary">
                  Estimated Tax
                </Text>
                <Text fontSize="sm" fontWeight="semibold">
                  {getCurrencySymbol(currency)}
                  {estimatedTax.toFixed(2)}
                </Text>
              </HStack>

              <Divider />

              <HStack justify="space-between">
                <Text fontSize="sm" fontWeight="semibold">
                  Grand Total
                </Text>
                <Text fontSize="sm" fontWeight="semibold">
                  {getCurrencySymbol(currency)}
                  {grandTotal.toFixed(2)}
                </Text>
              </HStack>
            </VStack>
          </GridItem>
        </Grid>
      </Stack>
    </Box>
  );
};

export default GroupOrderDetails;
